import { clsx } from '@alexis/helpers/clsx';
import { getCookie } from '@alexis/helpers/cookie';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { generateCUID } from '@alexis/helpers/identity';
import React, { useEffect, useRef, useState, PropsWithChildren, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ChevronIcon from '@icons/chevron-right-small.svg';

import { getUserState } from '@redux/selectors';

import styles from '../styles/components/Carousel.module.scss';

interface CarouselProps {
  className?: string;
  isRtl: boolean;
  itemLength: number;
  itemWidth: number;
  scrollWidth: number;
  isLightVersion?: boolean;
  autoScroll?: boolean;
  scrollDuration?: number;
}

const Carousel: React.FC<PropsWithChildren<CarouselProps>> = ({
  children,
  className,
  isRtl,
  itemLength,
  itemWidth,
  scrollWidth,
  isLightVersion = false,
  autoScroll = false,
  scrollDuration = 5,
}) => {
  const containerDivRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isItemOverflowed, setIsItemOverflowed] = useState<boolean>(false);
  const [maxScrollLeft, setMaxScrollLeft] = useState<number>(0);
  const [translateXPosition, setTranslateXPosition] = useState<number>(0);
  const [isPaused, setIsPaused] = useState(false);
  const user = useSelector(getUserState);
  const apiBaseUrl = API_BASE_URL;
  const params = useParams<{
    hotelId: string;
  }>();

  const pageViewId = useMemo<string>(() => {
    return generateCUID();
  }, []);

  useEffect(() => {
    const initializeScrollingValues = (): void => {
      if (!!containerDivRef.current) {
        const scrollWidth = itemLength * itemWidth;

        setIsItemOverflowed(scrollWidth > containerDivRef.current.clientWidth);
        setMaxScrollLeft((scrollWidth - containerDivRef.current.clientWidth) * (isRtl ? 1 : -1));
        setTranslateXPosition(0);
      }
    };

    window.addEventListener('resize', initializeScrollingValues);

    initializeScrollingValues();

    return () => {
      window.removeEventListener('resize', initializeScrollingValues);
    };
  }, [containerDivRef, itemLength, itemWidth, isRtl]);

  // Auto-scroll logic
  useEffect(() => {
    if (!autoScroll || !isItemOverflowed || isPaused) {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    const startAutoScroll = () => {
      autoScrollIntervalRef.current = setInterval(() => {
        setTranslateXPosition((currentPosition) => {
          // Calculate the width of one item for scrolling
          const oneItemWidth = itemWidth;

          if (isRtl) {
            // RTL: move right (positive direction)
            const proposedPosition = currentPosition + oneItemWidth;
            // If we would overshoot the end, first clamp to the end,
            // then on the next tick reset back to start.
            if (proposedPosition > maxScrollLeft) {
              return currentPosition === maxScrollLeft ? 0 : maxScrollLeft;
            }
            return proposedPosition;
          } else {
            // LTR: move left (negative direction)
            const proposedPosition = currentPosition - oneItemWidth;
            // If we would overshoot the end, first clamp to the end,
            // then on the next tick reset back to start.
            if (proposedPosition < maxScrollLeft) {
              return currentPosition === maxScrollLeft ? 0 : maxScrollLeft;
            }
            return proposedPosition;
          }
        });
      }, scrollDuration * 1000);
    };

    startAutoScroll();

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };
  }, [autoScroll, isItemOverflowed, isPaused, maxScrollLeft, itemWidth, isRtl, scrollDuration]);

  // Function to temporarily pause auto-scroll when user manually navigates
  const pauseAutoScrollTemporarily = useCallback(() => {
    if (autoScroll) {
      setIsPaused(true);
      setTimeout(() => setIsPaused(false), scrollDuration * 1000); // Resume after 1 cycle
    }
  }, [autoScroll, scrollDuration]);

  const handledGenzoActionEvent = useCallback(
    (value: string): void => {
      const wegoAnalyticsClientIdCookie = getCookie('wego_analytics_client_id');
      const wegoAnalyticsClientSessionIdCookie = getCookie('wego_analytics_client_session_id');
      if (isLightVersion && !!wegoAnalyticsClientIdCookie && !!wegoAnalyticsClientSessionIdCookie) {
        const eventData = {
          id: pageViewId,
          category: 'review_carousel_top_hoteldetails_dweb',
          object: 'hoteldetails_page',
          action: 'click',
          value: `${value}&${parseInt(params.hotelId!)}`,
        };
        genzoTrackActionEvent(
          apiBaseUrl,
          wegoAnalyticsClientIdCookie,
          wegoAnalyticsClientSessionIdCookie,
          user?.userHash,
          eventData,
        );
      }
    },
    [user, apiBaseUrl, pageViewId, params.hotelId, isLightVersion],
  );

  function handleLeftChevronClick(isRtl: boolean): void {
    if (isLightVersion) handledGenzoActionEvent('previous');

    pauseAutoScrollTemporarily();

    if (isRtl) {
      setTranslateXPosition(Math.max(translateXPosition - scrollWidth, 0));
    } else {
      setTranslateXPosition(Math.min(translateXPosition + scrollWidth, 0));
    }
  }

  function handleRightChevronClick(isRtl: boolean): void {
    if (isLightVersion) handledGenzoActionEvent('next');

    pauseAutoScrollTemporarily();

    if (isRtl) {
      setTranslateXPosition(Math.min(translateXPosition + scrollWidth, maxScrollLeft));
    } else {
      setTranslateXPosition(Math.max(translateXPosition - scrollWidth, maxScrollLeft));
    }
  }

  // Mouse event handlers for pausing auto-scroll on hover
  const handleMouseEnter = () => {
    if (autoScroll) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (autoScroll) {
      setIsPaused(false);
    }
  };

  return (
    <div
      ref={containerDivRef}
      className={clsx(styles.container, className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.scrollPane}>
        <div
          style={{
            transform: `translateX(${translateXPosition}px)`,
          }}
          className={clsx(autoScroll && styles.autoScrollTransition)}
        >
          {children}
        </div>
      </div>

      <button
        aria-label='Previous'
        type='button'
        className={styles.left}
        disabled={translateXPosition === 0 || !isItemOverflowed}
        onClick={() => handleLeftChevronClick(isRtl)}
      >
        <ChevronIcon className={clsx(styles.chevron, styles.left)} />
      </button>

      <button
        aria-label='Next'
        type='button'
        className={styles.right}
        disabled={translateXPosition === maxScrollLeft || !isItemOverflowed}
        onClick={() => handleRightChevronClick(isRtl)}
      >
        <ChevronIcon className={styles.chevron} />
      </button>
    </div>
  );
};

export default Carousel;
